import { AuctionSessionsResponseSchema, type AuctionSessionsResponse } from '@carsfinders/contracts';

import { apiFetch } from '../../lib/api';
import { type AuctionScheduleApiResponse, type GetAuctionScheduleParams } from './types';

/**
 * Auction-schedule API — hits api-node directly (ported from the web RTK Query
 * `auctionScheduleApi`). Endpoint consumed: GET /admin/auction-schedule
 * (@Roles('admin'), byte-compatible with the web admin route).
 */
export function fetchAuctionSchedule(
  params: GetAuctionScheduleParams = {},
): Promise<AuctionScheduleApiResponse> {
  const searchParams: Record<string, string | undefined> = {
    timeframe: params.filters?.timeframe,
    status: params.filters?.status,
    search: params.filters?.search || undefined,
    page: params.page?.toString(),
    limit: params.limit?.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };
  return apiFetch<AuctionScheduleApiResponse>('admin/auction-schedule', { searchParams });
}

/**
 * The Design D session-schedule feed: auction SESSIONS in a date window.
 *
 * Deliberately NOT `admin/auction-schedule` above, which is per-lot and starts
 * from the `bids` collection — this is the same endpoint the web auction app
 * consumes (`apps/auction/.../api/auctionScheduleApi.ts`'s `getAuctionSessions`),
 * hit directly against api-node the way every other admin fetch is (see
 * `apps/admin/src/lib/env.ts`).
 *
 * The admin SPA has no RTK Query store (there is no `@reduxjs/toolkit`
 * dependency anywhere in `apps/admin`; every other admin feature fetches with
 * plain `@tanstack/react-query` over an `apiFetch` call — see e.g.
 * `../bids/api.ts`), so this follows that established convention rather than
 * introducing RTK Query for one endpoint. The response is still validated
 * through the contract's own zod schema, exactly like the web original: the
 * type declares `startsAt` / `endsAt` / `lastUpdated` as `Date`, but JSON
 * carries strings, and `z.coerce.date()` is what makes that true rather than
 * a cast lying about it.
 *
 * `scope=bids` (owner decision 2026-10-12): the admin schedule shows only lots
 * with standing client bids, each with its close time, bid count, leader and
 * relay status (`session.bidLots`) — not the whole catalogue. The server's
 * default (no scope) response is left as it was for any other consumer.
 */
export async function fetchAuctionSessions(params: { from: string; to: string }): Promise<AuctionSessionsResponse> {
  const raw = await apiFetch<unknown>('auction-sessions', {
    searchParams: { from: params.from, to: params.to, scope: 'bids' },
  });

  const parsed = AuctionSessionsResponseSchema.safeParse(raw);
  if (!parsed.success) {
    // A contract drift surfaces to the operator as the generic error panel,
    // whose retry button will fail identically forever. Without this line
    // there is nothing anywhere saying WHY — `isAccessDeniedError` is false
    // for a ZodError, so it never reaches the <AccessDenied /> branch either.
    console.error('GET /auction-sessions does not match AuctionSessionsResponseSchema:', parsed.error.issues);
    throw parsed.error;
  }

  return parsed.data;
}
